/**
 * QueueHealth — per-queue health (route `queues.health`, param q=<queueId>),
 * opened from the ⋯ menu on a queue in the Queues tab: wait/service metrics vs
 * targets & forecast, per-service and per-staff breakdowns, issue alerts with
 * "Ask for a recommendation" (getQueueRecommendation — awaited). Without a
 * queue param it shows the location-wide rollup.
 */
import { useState } from "react";
import { alpha, Btn, Icon, Pill, T } from "@v0y4ge/jrni-ui";
import { Screen, PageHeader } from "../../shell/chrome";
import { DataSourceStrip } from "../../shell/opSource";
import { StatTile } from "../../shell/composites";
import { useIsMobile } from "../../shell/useResponsive";
import { useNav } from "../../shell/router";
import { useWfResource } from "../workforce/useWorkforce";
import { LocationFilter, WfPending } from "../workforce/wf-shared";
import { getQueueHealth, getQueueRecommendation, listQueues, type QueueHealth as HealthT, type QueueSummary, type QueueRecommendation, type QueueListPage } from "./queues-api";

import { useT } from "@v0y4ge/i18n-runtime";
const RANGES = [["today", "Today"], ["7d", "7 days"], ["30d", "30 days"]] as const;

export function RecommendationCard({ rec, onDismiss }: { rec: QueueRecommendation; onDismiss: () => void }) {
  const t = useT();
  return (
    <div data-testid="qrec-card" style={{ border: `1px solid ${T.p3}`, background: T.p4, borderRadius: 10, padding: 14, display: "flex", flexDirection: "column", gap: 8 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ width: 26, height: 26, borderRadius: 7, background: T.p1, color: T.white, display: "inline-flex", alignItems: "center", justifyContent: "center" }}><Icon.msg size={14}/></span>
        <span style={{ fontSize: 13, fontWeight: 700, color: T.n1, flex: 1 }}>{t("queues.health.text", "Recommendation")}</span>
        <Pill tone={rec.confidence === "high" ? "success" : rec.confidence === "medium" ? "warn" : "neutral"}>{rec.confidence} {t("queues.health.text2", "confidence")}</Pill>
      </div>
      <div style={{ fontSize: 13, color: T.n1, lineHeight: 1.55 }}>{rec.summary}</div>
      <ul style={{ margin: 0, padding: "0 0 0 18px", display: "flex", flexDirection: "column", gap: 4 }}>
        {rec.actions.map((a, i) => <li key={i} style={{ fontSize: 12.5, color: T.n2, lineHeight: 1.5 }}>{a}</li>)}
      </ul>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ fontSize: 11, color: T.soft, flex: 1 }}>{t("queues.health.text3", "Based on")}{" "}{rec.basedOn}</span>
        <Btn variant="secondary" onClick={onDismiss}>{t("queues.health.text4", "Dismiss")}</Btn>
      </div>
    </div>
  );
}

export default function QueueHealth() {
  const t = useT();
  const nav = useNav();
  const isMobile = useIsMobile();
  const queueId = (nav.params?.q ?? null) as string | null;
  const { data: queuesData } = useWfResource<QueueListPage>("queues|all", () => listQueues(null));
  const queueName = queueId ? ((queuesData?.data ?? []).find((x) => x.id === queueId)?.name ?? "Queue") : null;
  const [locationId, setLocationId] = useState<string | null>(null);
  const [range, setRange] = useState<string>("today");
  const { data, loading, error, reload } = useWfResource<HealthT>(`qhealth|${range}|${locationId ?? "all"}|${queueId ?? "-"}`, () => getQueueHealth(range, locationId, queueId));
  const [recFor, setRecFor] = useState<string | null>(null);
  const [rec, setRec] = useState<QueueRecommendation | null>(null);
  const [recBusy, setRecBusy] = useState(false);

  const ask = async (alertId: string, context: string) => {
    setRecBusy(true); setRecFor(alertId); setRec(null);
    try {
      const r = await getQueueRecommendation({ alertId, context });
      setRec(r.recommendation);
    } catch { setRecFor(null); }
    setRecBusy(false);
  };

  const k = data?.kpis;
  const svcDelta = k ? k.avgServiceMins - k.forecastServiceMins : 0;
  const th = { textAlign: "left" as const, padding: "8px 12px", fontSize: 11, fontWeight: 700, color: T.soft, textTransform: "uppercase" as const, letterSpacing: .04, borderBottom: `1px solid ${T.border}` };
  const td = { padding: "10px 12px", fontSize: 13, color: T.n1, borderBottom: `1px solid ${T.border}` };

  return (
    <Screen rail="q-board">
      <PageHeader title={queueName ? t("queues.health.title", "Queue health — {queueName}", { queueName }) : t("queues.health.title2", "Queue health")} subtitle={t("queues.health.subtitle", "Wait and service performance vs your targets — by service and by staff member.")}
        breadcrumb={<span style={{ cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4 }} onClick={() => nav.go("queues")}><Icon.arrow dir="left" size={11} /> {t("queues.health.text5", "Queues")}</span>}
        actions={<>
          <div role="group" aria-label={t("queues.health.ariaLabel", "Range")} style={{ display: "inline-flex", border: `1px solid ${T.border}`, borderRadius: 9, overflow: "hidden" }}>
            {RANGES.map(([id, label]) => (
              <button key={id} onClick={() => setRange(id)} aria-pressed={range === id} data-testid={`qhealth-range-${id}`}
                style={{ height: 36, padding: "0 14px", border: "none", cursor: "pointer", fontFamily: T.font, fontSize: 12.5, fontWeight: 600, background: range === id ? T.n1 : T.white, color: range === id ? T.white : T.n2 }}>{label}</button>
            ))}
          </div>
          {!queueId ? <LocationFilter value={locationId} onChange={setLocationId} /> : null}
        </>} />
      <DataSourceStrip screen="qhealth" ops={["getQueueHealth", "getQueueRecommendation"]} />

      {error ? <div style={{ padding: "16px 24px" }}><WfPending screen="qhealth" opName="getQueueHealth" error={error} onRetry={reload} /></div>
      : loading || !data ? <div style={{ padding: 60, textAlign: "center", color: T.soft, fontSize: 14 }}>{t("queues.health.text6", "Loading queue health…")}</div>
      : (
        <div style={{ padding: "16px 24px 24px", flex: 1, overflow: "auto", display: "flex", flexDirection: "column", gap: 18 }}>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "repeat(2, 1fr)" : "repeat(6, 1fr)", gap: 12 }}>
            <StatTile label={t("queues.health.label", "Avg wait")} value={`${k!.avgWaitMins}m`} delta={`target ${k!.slaTargetMins}m`} deltaPos={k!.avgWaitMins <= k!.slaTargetMins} icon={<Icon.clock size={14}/>} accent={k!.avgWaitMins > k!.slaTargetMins ? T.orange1 : T.green1} />
            <StatTile label={t("queues.health.label2", "p90 wait")} value={`${k!.p90WaitMins}m`} icon={<Icon.warn size={14}/>} accent={k!.p90WaitMins > k!.slaTargetMins * 2 ? T.red1 : T.n2} />
            <StatTile label={t("queues.health.label3", "Avg service")} value={`${k!.avgServiceMins}m`} delta={`${svcDelta >= 0 ? "+" : ""}${svcDelta}m vs forecast`} deltaPos={svcDelta <= 0} icon={<Icon.user size={14}/>} accent={T.tDarkBlue} />
            <StatTile label={t("queues.health.label4", "Left unserved")} value={`${k!.abandonPct}%`} icon={<Icon.x size={14}/>} accent={k!.abandonPct > 8 ? T.red1 : T.n2} />
            <StatTile label="CSAT" value={k!.csat.toFixed(1)} delta="of 5, from follow-ups" deltaPos={k!.csat >= 4.2} icon={<Icon.check size={14}/>} accent={T.tPurple} /> {/* ui-config — CSAT: metric acronym */}
            <StatTile label={t("queues.health.label5", "Served")} value={k!.served} icon={<Icon.users size={14}/>} accent={T.p1} />
          </div>

          {/* Wait trend */}
          <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 10, padding: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: T.n1, marginBottom: 12 }}>{t("queues.health.text7", "Wait time through the")}{range === "today" ? t("queues.health.text8", "day") : t("queues.health.text9", "period")}</div>
            <div style={{ display: "flex", alignItems: "flex-end", gap: 3, height: 90 }}>
              {data.trend.map((p, i) => {
                const max = Math.max(...data.trend.map((x) => x.wait), 1);
                const over = p.wait > k!.slaTargetMins;
                return <div key={i} title={t("queues.health.title3", "{t} · {wait}m", { t: p.t, wait: p.wait })} style={{ flex: 1, height: `${Math.max(4, (p.wait / max) * 100)}%`, borderRadius: "3px 3px 0 0", background: over ? T.orange1 : alpha(T.p1, 55) }} />;
              })}
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10.5, color: T.soft, marginTop: 6 }}>
              <span>{data.trend[0]?.t}</span><span>{data.trend[data.trend.length - 1]?.t}</span>
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 16, alignItems: "start" }}>
            {/* By service */}
            <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 10, overflow: "hidden" }}>
              <div style={{ padding: "12px 16px", fontSize: 13, fontWeight: 700, color: T.n1, borderBottom: `1px solid ${T.border}` }}>{t("queues.health.text10", "Services — actual vs forecast")}</div>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead><tr><th style={th}>{t("queues.health.colService", "Service")}</th><th style={th}>{t("queues.health.colServed", "Served")}</th><th style={th}>{t("queues.health.colAvgTime", "Avg time")}</th><th style={th}>{t("queues.health.colForecast", "Forecast")}</th><th style={th}>Δ</th></tr></thead> {/* ui-config — Δ: math symbol; CSAT handled above */}
                <tbody>
                  {data.byService.map((s) => {
                    const d = s.avgServiceMins - s.forecastMins;
                    return (
                      <tr key={s.service} data-testid="qhealth-service-row" data-id={s.service}>
                        <td style={{ ...td, fontWeight: 600 }}>{s.service}</td>
                        <td style={{ ...td, fontFamily: T.fontMono }}>{s.served}</td>
                        <td style={{ ...td, fontFamily: T.fontMono }}>{s.avgServiceMins}{t("queues.health.text11", "m")}</td>
                        <td style={{ ...td, fontFamily: T.fontMono, color: T.soft }}>{s.forecastMins}{t("queues.health.text11", "m")}</td>
                        <td style={{ ...td, fontFamily: T.fontMono, fontWeight: 700, color: d > 2 ? T.red1 : d < -2 ? T.green1 : T.n2 }}>{d >= 0 ? "+" : ""}{d}{t("queues.health.text11", "m")}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {/* By staff */}
            <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 10, overflow: "hidden" }}>
              <div style={{ padding: "12px 16px", fontSize: 13, fontWeight: 700, color: T.n1, borderBottom: `1px solid ${T.border}` }}>{t("queues.health.text12", "Staff — speed vs the team")}</div>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead><tr><th style={th}>{t("queues.health.colStaffMember", "Staff member")}</th><th style={th}>{t("queues.health.colServed", "Served")}</th><th style={th}>{t("queues.health.colAvgTime", "Avg time")}</th><th style={th}>{t("queues.health.colTeamAvg", "Team avg")}</th><th style={th}>CSAT</th></tr></thead> {/* ui-config — CSAT: metric acronym */}
                <tbody>
                  {data.byStaff.map((s) => {
                    const d = s.avgServiceMins - s.teamAvgMins;
                    return (
                      <tr key={s.name} data-testid="qhealth-staff-row" data-id={s.name}>
                        <td style={{ ...td, fontWeight: 600 }}>{s.name}</td>
                        <td style={{ ...td, fontFamily: T.fontMono }}>{s.served}</td>
                        <td style={{ ...td, fontFamily: T.fontMono, color: d > 3 ? T.orange1 : T.n1 }}>{s.avgServiceMins}{t("queues.health.text11", "m")}</td>
                        <td style={{ ...td, fontFamily: T.fontMono, color: T.soft }}>{s.teamAvgMins}{t("queues.health.text11", "m")}</td>
                        <td style={{ ...td, fontFamily: T.fontMono }}>{s.csat.toFixed(1)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Alerts + recommendations */}
          <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 10, padding: 16, display: "flex", flexDirection: "column", gap: 10 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: T.n1 }}>{t("queues.health.text13", "Issues needing attention")}</div>
            {data.alerts.length === 0 ? <div style={{ fontSize: 12.5, color: T.soft }}>{t("queues.health.text14", "All clear — no threshold breaches in this range.")}</div> : data.alerts.map((a) => (
              <div key={a.id} data-testid="qhealth-alert" data-id={a.id} style={{ border: `1px solid ${a.severity === "critical" ? alpha(T.red1, 35) : a.severity === "warn" ? alpha(T.orange1, 40) : T.border}`, borderRadius: 10, padding: 12, display: "flex", flexDirection: "column", gap: 8 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <Pill tone={a.severity === "critical" ? "danger" : a.severity === "warn" ? "warn" : "info"} dot>{a.severity === "critical" ? "Critical" : a.severity === "warn" ? "Warning" : "Info"}</Pill>
                  <span style={{ fontSize: 13.5, fontWeight: 700, color: T.n1, flex: 1 }}>{a.title}</span>
                  <Btn variant="secondary" onClick={() => ask(a.id, a.title)} testid="qhealth-ask-rec-btn" style={{ opacity: recBusy && recFor === a.id ? .6 : 1 }}>
                    {recBusy && recFor === a.id ? t("queues.health.thinking", "Thinking…") : t("queues.health.askForRecommendation", "Ask for a recommendation")}
                  </Btn>
                </div>
                <div style={{ fontSize: 12.5, color: T.n2, lineHeight: 1.5 }}>{a.body}</div>
                {recFor === a.id && rec ? <RecommendationCard rec={rec} onDismiss={() => { setRecFor(null); setRec(null); }} /> : null}
              </div>
            ))}
          </div>
        </div>
      )}
    </Screen>
  );
}
